import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import axios from 'axios';
import SessionHelper from "../components/Helper/SessionHelper";
import { reduxHistory as history } from '../store/configureStore';
import authConfig from '../config/auth';

//= ===============================
// Utility actions
//= ===============================
export function createError(message) {
  return {
    data: {
      message,
    },
  };
}

export function errorHandler(dispatch, error, type) {
  if (!error || error._sessionExpired) {
    return false;
  }

  Swal.fire({
    icon: "warning",
    text: error?.data?.message || 'An unexpected error occurred',
  });
}

let sessionExpiredShown = false;
function handleSessionExpired() {
  if (sessionExpiredShown) return;
  sessionExpiredShown = true;
  SessionHelper.logout();
  Swal.fire({
    icon: 'info',
    title: 'Session Expired',
    text: 'Your session has expired. Please log in again.',
    confirmButtonText: 'Log In',
    allowOutsideClick: false,
  }).then(() => {
    sessionExpiredShown = false;
    history.push('/login');
  });
}

export function generalErrorHandle(message) {
  Swal.fire({
    icon: "error",
    text: message,
  });
}

// Anonymous APIv2 Axios instance (no auth token) — for login, public endpoints
export const anonymousApiV2 = axios.create({
  baseURL: `${authConfig.apiV2Url}/api`,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// APIv2 Axios instance for Laravel API data operations
export const apiV2 = axios.create({
  baseURL: `${authConfig.apiV2Url}/api`,
  headers: {
    'Accept': 'application/json',
  },
});

// Rejection for a request whose login ended (logout or another sign-in,
// possibly in another tab) before it could be retried. It is dropped,
// never replayed with the new login's token.
function sessionChangedError(error) {
  const err = error || new Error('Authentication session changed');
  err._sessionChanged = true;
  // Keeps errorHandler quiet: the user already moved to another login.
  err._sessionExpired = true;
  if (err.response) err.response._sessionExpired = true;
  return err;
}

// APIv2 request interceptor — Bearer token
apiV2.interceptors.request.use(
  (config) => {
    // Remember which login this request belongs to. A retry keeps the
    // login it was first sent for: if that login has ended, the retry is
    // dropped here, before the current login's token is attached.
    const loginId = SessionHelper.getLoginId();
    if (config._loginId !== undefined && config._loginId !== loginId) {
      return Promise.reject(sessionChangedError());
    }
    config._loginId = loginId;

    const token = SessionHelper.getToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Token refresh state — one refresh at a time, and only for the login
// that started it: a refresh for an earlier login is never shared.
let refreshPromise = null;
let refreshPromiseLoginId = null;

const SESSION_CHANGED = 'session_changed';

function currentRefreshToken() {
  if (SessionHelper.isSsoSession()) return SessionHelper.getTtsRefreshToken();
  // Email sessions from before the session type was stored kept their
  // APIv2 refresh token under the Cognito key.
  return SessionHelper.getApiV2RefreshToken() || SessionHelper.getTtsRefreshToken();
}

// How long a failed email refresh waits for another tab of the same
// login to store the pair it rotated, before the session is expired.
export const ROTATION_WAIT_MS = 2000;
const ROTATION_POLL_MS = 100;

async function waitForRotationByOtherTab(loginId, usedRefreshToken) {
  for (let waited = 0; waited < ROTATION_WAIT_MS; waited += ROTATION_POLL_MS) {
    if (SessionHelper.getLoginId() !== loginId) return false;
    if (currentRefreshToken() !== usedRefreshToken) return true;
    await new Promise((resolve) => setTimeout(resolve, ROTATION_POLL_MS));
  }
  return SessionHelper.getLoginId() === loginId && currentRefreshToken() !== usedRefreshToken;
}

// Email/password login: rotate the APIv2 refresh token.
async function refreshEmailSession(refreshToken) {
  const response = await fetch(`${authConfig.apiV2Url}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (!response.ok) return null;

  const data = await response.json();
  if (!data.access_token || !data.refresh_token) return null;
  return { accessToken: data.access_token, refreshToken: data.refresh_token };
}

// SSO login: refresh the Cognito tokens, then exchange the new ID token
// for a fresh local JWT via APIv2.
async function refreshSsoSession(refreshToken) {
  const response = await fetch(`${authConfig.ttsAuthUrl}/api/v1/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      refreshToken,
      clientId: authConfig.clientId,
    }),
  });

  if (!response.ok) return null;

  const data = await response.json();
  const idToken = data.data?.idToken || data.idToken;
  const newRefreshToken = data.data?.refreshToken || data.refreshToken || refreshToken;

  if (!idToken) return null;

  const ssoResponse = await fetch(`${authConfig.apiV2Url}/api/admin/sso-login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id_token: idToken }),
  });

  if (!ssoResponse.ok) return null;

  const authData = await ssoResponse.json();
  if (!authData.access_token) return null;
  return { accessToken: authData.access_token, refreshToken: newRefreshToken };
}

// Returns the new access token, null when the refresh failed, or
// SESSION_CHANGED when the login changed meanwhile (nothing is stored).
async function refreshAccessToken(loginId) {
  const sso = SessionHelper.isSsoSession();
  const refreshToken = currentRefreshToken();
  if (!refreshToken) return null;

  const tokens = sso ? await refreshSsoSession(refreshToken) : await refreshEmailSession(refreshToken);

  if (!tokens && !sso) {
    // APIv2 revokes a refresh token when it rotates it, so this fails when
    // another tab of this login refreshed first. Use the tokens that tab
    // stores (it may still be writing them).
    if (await waitForRotationByOtherTab(loginId, refreshToken)) return SessionHelper.getToken() || null;
  }

  if (SessionHelper.getLoginId() !== loginId) return SESSION_CHANGED;
  if (!tokens) return null;

  // Store the new tokens (the request interceptor reads getToken()). The
  // access token goes first: other tabs treat a changed refresh token as
  // "the new pair is stored".
  SessionHelper.setToken(tokens.accessToken);
  if (sso) {
    SessionHelper.setTtsRefreshToken(tokens.refreshToken);
  } else {
    SessionHelper.setApiV2RefreshToken(tokens.refreshToken);
    // Drop a pre-upgrade copy kept under the Cognito key.
    SessionHelper.clearLegacyTtsRefreshToken();
  }
  return tokens.accessToken;
}

function refreshForLogin(loginId) {
  if (!refreshPromise || refreshPromiseLoginId !== loginId) {
    const promise = refreshAccessToken(loginId).finally(() => {
      if (refreshPromise === promise) {
        refreshPromise = null;
        refreshPromiseLoginId = null;
      }
    });
    refreshPromise = promise;
    refreshPromiseLoginId = loginId;
  }
  return refreshPromise;
}

function expireSession(error) {
  handleSessionExpired();
  error._sessionExpired = true;
  if (error.response) error.response._sessionExpired = true;
  return Promise.reject(error);
}

// Response error interceptor for 401 handling with automatic token refresh
apiV2.interceptors.response.use(response => response, async (error) => {
  const originalRequest = error.config;

  if (error.response?.status !== 401) {
    return Promise.reject(error);
  }

  // A 401 for a request sent under an earlier login says nothing about
  // the current one: reject it without touching auth.
  const loginId = originalRequest?._loginId;
  if (originalRequest && loginId !== SessionHelper.getLoginId()) {
    return Promise.reject(sessionChangedError(error));
  }

  if (!originalRequest || originalRequest._retried || !currentRefreshToken()) {
    return expireSession(error);
  }

  originalRequest._retried = true;

  let result;
  try {
    result = await refreshForLogin(loginId);
  } catch (refreshError) {
    result = SessionHelper.getLoginId() === loginId ? null : SESSION_CHANGED;
  }

  if (result === SESSION_CHANGED || SessionHelper.getLoginId() !== loginId) {
    return Promise.reject(sessionChangedError(error));
  }
  if (!result) {
    return expireSession(error);
  }
  return apiV2(originalRequest);
});
